import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import App from './App';
import { useAuthStore } from './stores/authStore';
import { authApi } from './lib/api/auth.api';
import './lib/i18n/config'; // Initialize i18n
import './index.css';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
      retry: 3,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Initialize auth on app start: refresh if token expiring soon.
// Profile loading is handled inside App (via useAuthStore.loadProfile effect).
const token = globalThis.localStorage?.getItem('token');
if (token) {
  (async () => {
    try {
      if (authApi.isAccessTokenExpiringWithin(60)) {
        const refreshed = await authApi.refreshToken();
        if (refreshed) {
          useAuthStore.getState().setToken(authApi.getAccessToken());
        }
      }
    } catch {
      authApi.clearTokens();
    }
  })();
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </React.StrictMode>
);
